import { useCallback, useEffect, useRef } from 'react';
import { openEmptyChatModal, setSendMessageTo, setUnreadMessageCount } from 'src/redux/chat/reducer';
import { useAppDispatch } from 'src/redux/hooks';
import { FirebaseFirestore } from '@capacitor-firebase/firestore';
import { StreamChatMessagesContext } from '.';
import { isPlatform } from '@ionic/react';
import { useHistory, generatePath } from 'react-router';
import { environment } from 'src/enviroments';
import i18n from 'src/i18n';
import { fetchChatRooms } from 'src/redux/chat/thunk';
import { PrivateRoutes } from 'src/routes/navigation';
import { MindlyStreamChatCapacitor } from '@mindly/stream-chat-capacitor';
import { useSelector } from 'react-redux';
import { chatChannelsSelector, isStreamChatEnabledSelector } from 'src/redux/chat/selector';
import { userSelector } from 'src/redux/user/selector';
import { PluginListenerHandle } from '@capacitor/core';
import { getStreamChatToken } from 'src/api/stream/auth';
import { transformClientIdToStreamUserId } from 'src/shared/utils/transformClientIdToStreamUserId';

export const StreamChatMessagesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const history = useHistory();
  const dispatch = useAppDispatch();

  const channels = useSelector(chatChannelsSelector);
  const isStreamChatEnabled = useSelector(isStreamChatEnabledSelector);
  const user = useSelector(userSelector);

  const callbackIdFromUnreadMessagesListenerRef = useRef<string>();
  const listenerRef = useRef<PluginListenerHandle | null>(null);

  /**
   * Unread messages listener
   */
  const setupUnreadChatMessagesListener = useCallback(
    async (userId: string) => {
      if (isStreamChatEnabled === false) {
        return;
      }
      if (callbackIdFromUnreadMessagesListenerRef.current) {
        return;
      }

      callbackIdFromUnreadMessagesListenerRef.current = await FirebaseFirestore.addCollectionSnapshotListener(
        {
          reference: 'ChatMessagesMetadata',
          compositeFilter: {
            type: 'and',
            queryConstraints: [
              {
                type: 'where',
                fieldPath: 'client_id',
                opStr: '==',
                value: userId,
              },
            ],
          },
          queryConstraints: [
            {
              type: 'limit',
              limit: 1,
            },
          ],
        },
        (event, error) => {
          if (error) {
            console.error('[Firestore Listener] Error:', error);
          } else {
            const documents = event?.snapshots ?? [];
            if (documents.length > 0) {
              const data = documents?.[0]?.data;
              const unreadCount = data?.total_unread_message_count || 0;
              dispatch(setUnreadMessageCount(unreadCount));
            } else {
              dispatch(setUnreadMessageCount(0));
            }
          }
        }
      );
    },
    [user?.id, dispatch, isStreamChatEnabled]
  );

  const clearUnreadChatMessagesListener = useCallback(() => {
    if (callbackIdFromUnreadMessagesListenerRef.current) {
      FirebaseFirestore.removeSnapshotListener({
        callbackId: callbackIdFromUnreadMessagesListenerRef.current,
      });
      callbackIdFromUnreadMessagesListenerRef.current = undefined;
    }
  }, []);

  /**
   * Token listener
   */
  const setupTokenListener = useCallback(async () => {
    if (isStreamChatEnabled === false) return;

    const listener = await MindlyStreamChatCapacitor.addListener('requestToken', async () => {
      try {
        const token = await getStreamChatToken();
        await MindlyStreamChatCapacitor.provideToken({ token });
      } catch (error: any) {
        await MindlyStreamChatCapacitor.provideToken({ error: error.message });
      }
    });
    listenerRef.current = listener;
  }, [isStreamChatEnabled]);

  const clearTokenListener = useCallback(() => {
    listenerRef.current?.remove();
  }, []);

  const handleChatClick = useCallback(
    async (memberId?: string, presentingElement?: HTMLElement) => {
      if (isStreamChatEnabled === false) {
        history.push(memberId ? generatePath(PrivateRoutes.CHAT_ROOM, { id: memberId }) : PrivateRoutes.CHATS);
        return;
      }

      let channelId: string | undefined;
      try {
        if (!memberId && channels.length === 0) {
          const newChannelsData = await dispatch(fetchChatRooms()).unwrap();
          if (!newChannelsData.length) {
            dispatch(openEmptyChatModal());
            return;
          }
        }

        if (memberId) {
          const channel = channels.find((c) => c.members.includes(memberId));
          if (channel?.id) {
            channelId = channel?.id;
          } else {
            dispatch(setSendMessageTo({ id: memberId, presentingElement }));
            return;
          }
        }

        if (isPlatform('hybrid')) {
          const presentChatMethod = channelId
            ? MindlyStreamChatCapacitor.presentChannel
            : MindlyStreamChatCapacitor.presentChat;
          const transformedClientId = transformClientIdToStreamUserId(user?.id ?? '');

          presentChatMethod({
            apiKey: environment.STREAM_API_TOKEN,
            userInfo: {
              id: transformedClientId,
              name: `${user?.display_name} ${user?.last_name}`,
              imageURL: user?.avatar ?? '',
              language: i18n.language,
            },
            channelId: channelId,
          });
        } else {
          history.push(PrivateRoutes.STREAM_CHAT_CONVERSATION_LIST, { channelId });
        }
      } catch (error) {
        console.error('Error opening chat:', error);
      }
    },
    [user?.id, user?.display_name, user?.avatar, i18n.language, channels, isStreamChatEnabled]
  );

  useEffect(() => {
    if (!user?.id || isStreamChatEnabled === false) return;

    setupUnreadChatMessagesListener(user.id);
    setupTokenListener();

    return () => {
      clearUnreadChatMessagesListener();
      clearTokenListener();
    };
  }, [user?.id, isStreamChatEnabled]);

  return (
    <StreamChatMessagesContext.Provider value={{ handleChatClick }}>{children}</StreamChatMessagesContext.Provider>
  );
};
